import type { KnowledgeBaseDocumentApi } from '@liks/backend-svc-api/esm/apis/KnowledgeBaseDocumentApi';
import type {
  DocumentEmbeddingProgress,
  KnowledgeBaseDocument,
} from '@liks/backend-svc-api/esm/models/index';
import type { DocumentListOptions, WithResourceId } from '../types/knowledge';
import {
  feedDocumentEmbeddingProgressStreamBuffer,
  normalizeDocumentEmbeddingProgressResponse,
  parseDocumentEmbeddingProgressPayload,
} from './documentEmbeddingProgressStream';
import { hasId } from './knowledgeServiceGuards';

export async function listDocuments(
  documentApi: KnowledgeBaseDocumentApi,
  databaseId: string,
  options?: DocumentListOptions,
): Promise<Array<WithResourceId<KnowledgeBaseDocument>>> {
  const documents =
    await documentApi.deLiksKnowledgebaseBoundaryKnowledgeBaseDocumentResourceListDocuments(
      {
        knowledgeBaseId: databaseId,
        ...(options?.sort != null ? { sort: options.sort } : {}),
        ...(options?.direction != null ? { direction: options.direction } : {}),
        ...(options?.filename != null ? { filename: options.filename } : {}),
        ...(options?.source != null ? { source: options.source } : {}),
        ...(options?.status != null ? { status: options.status } : {}),
        ...(options?.publishState != null
          ? { publishState: options.publishState }
          : {}),
        ...(options?.limit != null ? { limit: options.limit } : {}),
        ...(options?.offset != null ? { offset: options.offset } : {}),
      },
    );
  return documents.filter(hasId);
}

export async function getDocument(
  documentApi: KnowledgeBaseDocumentApi,
  databaseId: string,
  documentId: string,
): Promise<WithResourceId<KnowledgeBaseDocument>> {
  const document =
    await documentApi.deLiksKnowledgebaseBoundaryKnowledgeBaseDocumentResourceReadDocument(
      { knowledgeBaseId: databaseId, id: documentId },
    );

  if (!hasId(document)) {
    throw new Error('Backend returned a document without id');
  }

  return document;
}

export async function uploadDocument(
  documentApi: KnowledgeBaseDocumentApi,
  databaseId: string,
  file: File,
): Promise<WithResourceId<KnowledgeBaseDocument>> {
  const document =
    await documentApi.deLiksKnowledgebaseBoundaryKnowledgeBaseDocumentResourceUploadDocument(
      {
        knowledgeBaseId: databaseId,
        data: file,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      },
    );

  if (!hasId(document)) {
    throw new Error('Backend returned a document without id');
  }

  return document;
}

export async function downloadDocument(
  documentApi: KnowledgeBaseDocumentApi,
  databaseId: string,
  documentId: string,
): Promise<Blob> {
  const response =
    await documentApi.deLiksKnowledgebaseBoundaryKnowledgeBaseDocumentResourceDownloadDocumentRaw(
      { knowledgeBaseId: databaseId, id: documentId },
    );
  return response.raw.blob();
}

export async function deleteDocument(
  documentApi: KnowledgeBaseDocumentApi,
  databaseId: string,
  documentId: string,
): Promise<void> {
  await documentApi.deLiksKnowledgebaseBoundaryKnowledgeBaseDocumentResourceDeleteDocument(
    { knowledgeBaseId: databaseId, id: documentId },
  );
}

export async function resumeAbandonedDocumentEmbedding(
  documentApi: KnowledgeBaseDocumentApi,
  databaseId: string,
  documentId: string,
): Promise<WithResourceId<KnowledgeBaseDocument>> {
  const document =
    await documentApi.deLiksKnowledgebaseBoundaryKnowledgeBaseDocumentResourceResumeAbandonedDocumentEmbedding(
      { knowledgeBaseId: databaseId, id: documentId },
    );

  if (!hasId(document)) {
    throw new Error('Backend returned a document without id');
  }

  return document;
}

export async function streamDocumentEmbeddingProgress(
  documentApi: KnowledgeBaseDocumentApi,
  databaseId: string,
  documentId: string,
  handlers: {
    onUpdate: (progress: DocumentEmbeddingProgress) => void;
    onComplete?: () => void;
  },
  signal?: AbortSignal,
): Promise<void> {
  const response =
    await documentApi.deLiksKnowledgebaseBoundaryKnowledgeBaseDocumentResourceStreamDocumentEmbeddingProgressRaw(
      { knowledgeBaseId: databaseId, id: documentId },
      async ({ init }) => ({
        ...init,
        headers: {
          ...init.headers,
          Accept: 'text/event-stream, application/x-ndjson, application/json',
        },
      }),
    );

  if (signal?.aborted) {
    return;
  }

  const body = response.raw.body;
  if (!body) {
    const fallback = await response.value();
    const normalized = normalizeDocumentEmbeddingProgressResponse(fallback);
    if (normalized) {
      handlers.onUpdate(normalized);
      if (normalized.status === 'READY' || normalized.status === 'FAILED') {
        handlers.onComplete?.();
      }
    }
    return;
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let pendingBuffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    pendingBuffer += decoder.decode(value, { stream: true });
    pendingBuffer = feedDocumentEmbeddingProgressStreamBuffer(
      pendingBuffer,
      handlers,
    );

    if (signal?.aborted) {
      await reader.cancel();
      return;
    }
  }

  pendingBuffer += decoder.decode();
  pendingBuffer = feedDocumentEmbeddingProgressStreamBuffer(
    pendingBuffer,
    handlers,
  );

  const trailing = parseDocumentEmbeddingProgressPayload(pendingBuffer);
  if (trailing) {
    handlers.onUpdate(trailing);
    if (trailing.status === 'READY' || trailing.status === 'FAILED') {
      handlers.onComplete?.();
    }
  }
}
